import React from 'react';

import { isoDay } from '../affiliates/reports-helpers';
import { fetchAnalyticsHosts, runAggregate, type AnalyticsHost } from './api';
import { pageViewsQuery, viewsByPath, type Span } from './queries';
import { postTrafficQuality } from './quality/api';
import { assetRequest, libraryBatches } from './quality/compile';
import { hostKindLabel } from './quality/hosts';
import { dataNotes } from './quality/notices';
import { enqueueQuality } from './quality/queue';
import type { QualityFacetAnswer, QualityFacetRequest, QualityKind, QualityNoHosts } from './quality/types';

/**
 * Views of a brand's landing pages: Pulsar page views on its landing addresses (`lp.` hosts), by page path, over a
 * period, read from the analytics service through spark-creatives (the Website analytics tab's own source). For the
 * landing page library's cards and stats sheet, which had no views before.
 */
export type LandingViewsStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'dash';

export type LandingViews = {
  /** Views per page path (no trailing slash); a listed page nobody opened reads 0, a page not read (yet) is absent. */
  byPath: ReadonlyMap<string, number>;
  /** `unavailable`: the analytics service is not set up here, or the first read failed (the cards show a dash); a
   *  failed re-read keeps the brand's last good counts. `dash`: quality mode has no count to show (no address to
   *  count, no landing address, or a range it cannot read), so the Views figure is a dash with `note`, never 0. */
  status: LandingViewsStatus;
  /** Said beside the Views dash (an English key); null: nothing. */
  note: string | null;
  /** The `{{n}}` the note reads, when it has one. */
  n?: number;
};

export type LandingViewsOptions = {
  /** The `website-analytics` feature is on; off, nothing is asked and the Views figure stays out. */
  enabled?: boolean;
  /** The brand reads the quality route: no legacy count is shown. */
  quality?: boolean;
  /** The asset sheet: its one page over its own dates (`span`), not the library's batches over 90 days. */
  asset?: boolean;
};

/** Pages per query: each path is asked for with and without its trailing slash, and an `in` takes 50 values. */
const PAGES_PER_QUERY = 25;
/** A brand's addresses are read once and shared briefly (every card and sheet asks for the same ones). */
const HOSTS_MS = 2 * 60 * 1000;

const hostsCache = new Map<number, { at: number; request: Promise<AnalyticsHost[] | null> }>();

/** The brand's addresses, or null when the analytics service is not set up here. Cached per brand. A failed read
 *  rejects (and is not kept). */
export const brandHosts = (tenantId: number): Promise<AnalyticsHost[] | null> => {
  const hit = hostsCache.get(tenantId);
  if (hit && Date.now() - hit.at < HOSTS_MS) return hit.request;
  const request = fetchAnalyticsHosts(tenantId)
    .then((answer) => (answer.configured ? answer.hosts : null))
    .catch((err: unknown) => {
      hostsCache.delete(tenantId);
      throw err;
    });
  hostsCache.set(tenantId, { at: Date.now(), request });
  return request;
};

/** What is said once beside a Views dash (its English key and the number it reads), or null when nothing is. Pure. */
export const viewsNote = (views: LandingViews): { text: string; placeholders?: { n: number } } | null => {
  if (views.status !== 'dash' || !views.note) return null;
  return views.n === undefined ? { text: views.note } : { text: views.note, placeholders: { n: views.n } };
};

/** A page path as the views are keyed: no trailing slash (the root stays "/"). Pure. */
export const viewsKey = (path: string): string => path.replace(/\/+$/, '') || '/';

const NONE: ReadonlyMap<string, number> = new Map();

const UNAVAILABLE: LandingViews = { byPath: NONE, status: 'unavailable', note: null };
const IDLE: LandingViews = { byPath: NONE, status: 'idle', note: null };
const LOADING: LandingViews = { byPath: NONE, status: 'loading', note: null };
const ASSET_MAX_DAYS = 366;

const isoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const askFacet = (
  tenantId: number,
  kind: QualityKind,
  body: QualityFacetRequest,
  signal: AbortSignal,
): Promise<QualityFacetAnswer | QualityNoHosts> =>
  enqueueQuality(tenantId, kind, () => postTrafficQuality(tenantId, body, signal), signal);

/** The dash an answer shows instead of counts, or null when its landing views can be shown. */
const dashOf = (answer: QualityFacetAnswer | QualityNoHosts): LandingViews | null => {
  if (!('meta' in answer)) return { byPath: NONE, status: 'dash', note: null };
  const selected = answer.scope.selectedHosts;
  if (!selected.length && answer.hostCatalog.defaultBasis === 'none') {
    const note = dataNotes(answer, false).find((n) => n.id === 4 || n.id === 5);
    return { byPath: NONE, status: 'dash', note: note?.text ?? null };
  }
  const kinds = new Map(answer.hosts.map((h) => [h.host, h.kind]));
  const landing = selected.some(
    (host) => hostKindLabel({ host, kind: kinds.get(host) ?? 'unknown' }) === 'Landing pages',
  );
  return landing ? null : { byPath: NONE, status: 'dash', note: null };
};

/** The answer's landing page views per candidate; a candidate it does not list counts 0. */
const countsOf = (answer: QualityFacetAnswer, keys: readonly string[]): Map<string, number> => {
  const out = new Map<string, number>(keys.map((k) => [k, 0]));
  if (answer.facet.name === 'page') answer.facet.rows.forEach((row) => out.set(viewsKey(row.path), row.pageViews));
  return out;
};

const candidatesOf = (request: QualityFacetRequest): string[] =>
  request.facet.name === 'page' && request.facet.kind === 'landing' ? request.facet.candidates ?? [] : [];

/**
 * The library's views in quality mode: its paths (keyed by {@link viewsKey}, in card order) in batches, one after
 * another. A failed batch leaves its pages out (a dash) and keeps the others; a read whose batches counted different
 * addresses is read again once, and is unavailable if they differ again. A ready read carries the addresses it counted
 * (`hosts`, sorted, one per line). Rejects only when `signal` aborts.
 */
export const readLibraryViews = async (
  tenantId: number,
  keys: readonly string[],
  signal: AbortSignal,
): Promise<LandingViews & { hosts?: string }> => {
  const batches = libraryBatches(keys);
  for (let pass = 0; pass < 2; pass += 1) {
    const out = new Map<string, number>();
    let hosts: string | null = null;
    let mixed = false;
    for (let i = 0; i < batches.length && !mixed; i += 1) {
      let answer: QualityFacetAnswer | QualityNoHosts;
      try {
        // eslint-disable-next-line no-await-in-loop -- one batch at a time: the service takes one request per tenant.
        answer = await askFacet(tenantId, `library:${i}`, batches[i], signal);
      } catch (err) {
        if (signal.aborted) throw err;
        continue;
      }
      const dash = dashOf(answer);
      if (dash || !('meta' in answer)) return dash ?? UNAVAILABLE;
      const seen = [...answer.scope.selectedHosts].sort().join('\n');
      if (hosts !== null && seen !== hosts) mixed = true;
      else {
        hosts = seen;
        countsOf(answer, candidatesOf(batches[i])).forEach((views, k) => out.set(k, views));
      }
    }
    // `hosts` is set once a batch answered.
    if (!mixed) return hosts !== null ? { byPath: out, status: 'ready', note: null, hosts } : UNAVAILABLE;
  }
  return UNAVAILABLE;
};

/**
 * The asset sheet's one page in quality mode, over the sheet's own dates (`span` from its first day's UTC midnight to
 * the one after its last day, unclipped), the last date held to the viewer's today; over 366 days nothing is asked.
 * Rejects only when `signal` aborts.
 */
export const readAssetViews = async (
  tenantId: number,
  key: string,
  span: Span,
  signal: AbortSignal,
  nowMs: number,
): Promise<LandingViews> => {
  // East of UTC the viewer's today is the next UTC date for a few hours, which the landing facet takes.
  const request = assetRequest(key, isoDate(span.from), isoDate(span.to - 1), isoDay(new Date(nowMs)));
  if (!request) return { byPath: NONE, status: 'dash', note: 'Up to {{n}} days.', n: ASSET_MAX_DAYS };
  let answer: QualityFacetAnswer | QualityNoHosts;
  try {
    answer = await askFacet(tenantId, 'asset', request, signal);
  } catch (err) {
    if (signal.aborted) throw err;
    return UNAVAILABLE;
  }
  const dash = dashOf(answer);
  if (dash || !('meta' in answer)) return dash ?? UNAVAILABLE;
  return { byPath: countsOf(answer, [key]), status: 'ready', note: null };
};

/** The views of `paths` over `span` (none asked without a brand, a path or a span). Only the newest read lands. */
export const useLandingPageViews = (
  tenantId: number | undefined,
  paths: readonly string[],
  span: Span | null,
  opts: LandingViewsOptions = {},
): LandingViews => {
  const enabled = opts.enabled ?? true;
  const quality = opts.quality ?? false;
  const asset = opts.asset ?? false;
  const from = span?.from;
  const to = span?.to;
  // The mode, brand and period counts are read for. Keyed on the mode too: a gate flip never shows the other mode's.
  const holding = `${quality ? `${asset ? 'asset' : 'library'}:person:unflagged` : 'legacy'}:${tenantId}:${from}:${to}`;
  /** `holding`: whose counts these are (another brand's, mode's or period's are never shown). `hosts` and `day`: the
   *  addresses a library read counted them over, and the UTC date it started on. */
  const [state, setState] = React.useState<LandingViews & { holding: string; hosts?: string; day?: string }>({
    holding: '',
    byPath: NONE,
    status: 'idle',
    note: null,
  });
  const readId = React.useRef(0);
  /** The brand and period whose counts `byPath` holds: another brand's or another period's are never kept or shown. */
  const heldFor = React.useRef('');
  const unique = [...new Set(paths.map(viewsKey))];
  // Quality mode asks in card order; the legacy read does not depend on the order.
  const pathsKey = quality ? unique.join('\n') : [...unique].sort().join('\n');
  const reads = Boolean(enabled && tenantId && pathsKey && from !== undefined && to !== undefined);

  // Syncs with the analytics service: the listed pages' views.
  React.useEffect(() => {
    readId.current += 1;
    const id = readId.current;
    if (!enabled || !tenantId || !pathsKey || from === undefined || to === undefined) {
      setState({ holding, byPath: NONE, status: 'idle', note: null });
      return undefined;
    }
    const controller = new AbortController();
    const live = (): boolean => id === readId.current && !controller.signal.aborted;
    const same = heldFor.current === holding;
    // The library's span is fixed when it mounts, while the service resolves its `90d` when each request arrives: counts
    // a read started on another UTC date cover another window.
    const day = quality && !asset ? isoDate(Date.now()) : undefined;
    setState((s) => ({
      holding,
      byPath: same ? s.byPath : NONE,
      hosts: same ? s.hosts : undefined,
      day: same ? s.day : undefined,
      status: 'loading',
      note: null,
    }));
    const keys = pathsKey.split('\n');
    if (quality) {
      const read: Promise<LandingViews & { hosts?: string }> = asset
        ? readAssetViews(tenantId, keys[0], { from, to }, controller.signal, Date.now())
        : readLibraryViews(tenantId, keys, controller.signal);
      read
        .then((next) => {
          if (!live()) return;
          // A failed re-read of the same brand, mode, period and read date keeps its last good counts.
          if (next.status === 'unavailable' && same) {
            setState((s) => (s.byPath !== NONE && s.day === day ? { ...s, status: 'ready' } : { holding, ...next }));
            return;
          }
          heldFor.current = next.status === 'ready' ? holding : '';
          if (next.status === 'ready' && same) {
            // A re-read where only some batches failed keeps the last good count of each page it could not read, when
            // it counted the same addresses on the same date: one result never mixes two host defaults or two windows.
            setState((s) => {
              if (s.hosts !== next.hosts || s.day !== day) return { holding, ...next, day };
              const byPath = new Map(next.byPath);
              keys.forEach((k) => {
                const last = s.byPath.get(k);
                if (!byPath.has(k) && last !== undefined) byPath.set(k, last);
              });
              return { holding, ...next, byPath, day };
            });
            return;
          }
          setState({ holding, ...next, day });
        })
        .catch(() => {
          if (live()) setState({ holding, ...UNAVAILABLE });
        });
      return () => controller.abort();
    }
    const run = async (): Promise<void> => {
      const hosts = await brandHosts(tenantId);
      if (!hosts) {
        if (live()) setState({ holding, byPath: NONE, status: 'unavailable', note: null });
        return;
      }
      const landing = hosts.filter((h) => h.kind === 'landing').map((h) => h.host);
      // The asset sheet's span runs to the end of its last day: the read stops at now.
      const until = Math.min(to, Date.now());
      const out = new Map<string, number>(keys.map((k) => [k, 0]));
      // A brand with no landing address has had no landing page visit.
      if (landing.length > 0) {
        for (let i = 0; i < keys.length; i += PAGES_PER_QUERY) {
          const chunk = keys.slice(i, i + PAGES_PER_QUERY);
          const values = chunk.flatMap((k) => (k === '/' ? ['/'] : [k, `${k}/`]));
          // eslint-disable-next-line no-await-in-loop -- one query at a time: the service's slots are shared.
          const answer = await runAggregate(
            tenantId,
            { ...pageViewsQuery({ from, to: until }, values, values.length), hosts: landing },
            controller.signal,
          );
          viewsByPath(answer.rows).forEach((row) => {
            const k = viewsKey(row.path);
            out.set(k, (out.get(k) ?? 0) + row.views);
          });
        }
      }
      if (!live()) return;
      heldFor.current = holding;
      setState({ holding, byPath: out, status: 'ready', note: null });
    };
    run().catch(() => {
      if (!live()) return;
      // A failed re-read of the same brand and period keeps its last good counts (a page they do not cover reads as
      // unknown); only a read with nothing held for them shows the dash.
      setState((s) =>
        same && s.byPath !== NONE
          ? { holding, byPath: s.byPath, status: 'ready', note: null }
          : { holding, byPath: NONE, status: 'unavailable', note: null },
      );
    });
    return () => controller.abort();
  }, [tenantId, pathsKey, from, to, enabled, quality, asset, holding]);

  const { holding: shownFor, hosts: _hosts, day: _day, ...shown } = state;
  if (!reads) return IDLE;
  return shownFor === holding ? shown : LOADING;
};
